import { Table, TableRow, TBody, TD, TH, THead } from '@components/DesignLibrary/Table';
import * as ApiModels from '@typings/api-models';
import * as format from '@format';
import X from '@components/Icons/X';
import SecondaryActions from '@components/DesignLibrary/SecondaryActions';
import { useEffect, useState } from 'react';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import priceWithCurrency from '@format/priceWithCurrency';
import SectionShimmer from '@lib/Creators/Dashboard/Dashboard/SectionShimmer';
import Assignees from './Assignees';
import WorklowContext from './WorklowContext';
import Workflow from './Workflow';
import Action from './Action';
import s from './TaskList.module.scss';

type ThisItem =
  & Pick<ApiModels.Task, 'id' | 'createdAt'>
  & Parameters<typeof Action>[0]['item']
  & Parameters<typeof Workflow>[0]['item']
  & Parameters<typeof WorklowContext>[0]['item']
  & {
    assignees: Parameters<typeof Assignees>[0]['assignees'];
    totalPaid: number;
  };

  type Props = {
    className?: string;
    enabledActions: Parameters<typeof Action>[0]['enabledActions'];
    isLoading?: boolean;
    sort?: {
      onSort: (sortBy: string, sortDirection: 'asc' | 'desc') => void;
      currentSortByField: string;
      currentSortDirection: 'asc' | 'desc';
    };
    onClickTask: (task: ThisItem) => void;
    tasks: Array<ThisItem>;
    title?: string;
    userId: string;
  };

const TaskTable: React.FC<Props> = ({
  className,
  enabledActions,
  isLoading,
  onClickTask,
  sort,
  tasks: initialTasks,
  title,
  userId,
}) => {

  const [tasks, setTasks] = useState(initialTasks);
  const { getData: dismissTask } = useNoodleApi(tsClient.tasks.dismissTask);

  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  const handleRemoveTask = async (removedTaskId: string): Promise<void> => {
    const response = await dismissTask({taskId: removedTaskId});

    if (!response.error) {
      setTasks(prevState => prevState.filter(task => task.id !== removedTaskId));
    }
  };

  const shimmerElements: JSX.Element[] = Array.from({ length: 5 }, (_, index) => (
    <SectionShimmer key={`todo-shimmer-${index}`} />
  ));

  return (
    <div className={className}>
      <Table>
        <THead>
          <TableRow>
            <TH label={title || 'Task'} />
            <TH label="Workflow"
              columnId='name'
              sort={sort}
            />
            <TH label="Context" />
            <TH label="Since"
              columnId='createdAt'
              sort={sort}
            />
            <TH label="Assignees" />
            <TH
              label="Total Amount Paid"
              columnId='totalPayments'
              sort={sort}
            />
            <TH label="" />
          </TableRow>
        </THead>
        <TBody>
          {!isLoading && tasks.length === 0 && <TableRow><TD label="No Tasks" colSpan={7} /></TableRow>}
          {!isLoading && tasks.map(task => (
            <TableRow key={task.id}>
              <TD>
                <Action item={task} userId={userId} onClick={() => onClickTask(task)} enabledActions={enabledActions} />
              </TD>
              <TD>
                <Workflow item={task} />
              </TD>
              <TD>
                <WorklowContext item={task} />
              </TD>
              <TD compact label={format.datetimeLocalTZ.withoutTimezone({ datetime: task.createdAt })} />
              <TD>
                <Assignees userId={userId} assignees={task.assignees} />
              </TD>
              <TD compact label={priceWithCurrency(task.totalPaid, ApiModels.Currency.Usd)} />
              <TD>
                <SecondaryActions
                  compact
                  ariaLabel='Task Options'
                  position='bottom-right'
                  options={[
                    { destructive: true, icon: X, value: 'Dismiss Task' },
                  ]}
                  onSelect={value =>
                  {
                    if (value === 'Dismiss Task') {
                      handleRemoveTask(task.id);
                    }
                  }}
                />
              </TD>
            </TableRow>
          ))}
        </TBody>
      </Table>
      {isLoading && <div className={s.shimmers}>
        {shimmerElements}
      </div>}
    </div>
  );
};
export default TaskTable;
